import React from 'react';
import { C } from '../../utils/styles';
import Icon from '../Icon';
import { formatRelative } from '../../utils/dashboardData';

function Avatar({ initials, color }) {
  return (
    <span style={{
      width: 32, height: 32, borderRadius: '50%',
      background: color || C.accent, color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 11, fontWeight: 800, flexShrink: 0,
      fontFamily: "'JetBrains Mono', monospace",
    }}>
      {initials}
    </span>
  );
}

function severityColor(severity) {
  if (severity === 'red') return { bg: '#fef2f2', fg: '#dc2626', dot: '#dc2626', label: '24h+ overdue' };
  if (severity === 'amber') return { bg: '#fffbeb', fg: '#d97706', dot: '#d97706', label: '4–24h' };
  return { bg: '#ecfdf5', fg: '#059669', dot: '#059669', label: 'Recent' };
}

export default function MessagesPanel({ summary, isSuperUser = false, onMessageClick, onAmClick }) {
  const data = summary || {};
  const yourMessages = data.myUnreadMessages || [];
  const unansweredByAm = data.unansweredByAm || [];
  const unansweredTotal = Number(data.unansweredTotal || 0);
  const unansweredAmCount = Number(data.unansweredAmCount || 0);

  return (
    <div
      data-testid="dashboard-messages"
      style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <Icon name="chatbubbles-outline" style={{ fontSize: 18, color: C.accent }} />
        <h3 style={{ fontSize: 11, fontWeight: 700, color: C.textLight, letterSpacing: '0.1em', textTransform: 'uppercase', margin: 0, fontFamily: "'JetBrains Mono', monospace" }}>
          Messages
        </h3>
      </div>

      {/* Super user: red unanswered banner */}
      {isSuperUser && unansweredTotal > 0 && (
        <div
          data-testid="messages-unanswered-alert"
          style={{
            background: '#fef2f2', border: '1px solid #fecaca', borderLeft: '4px solid #dc2626',
            borderRadius: 10, padding: '12px 14px', marginBottom: 14,
            display: 'flex', alignItems: 'center', gap: 12,
          }}
        >
          <Icon name="warning-outline" style={{ fontSize: 22, color: '#dc2626', flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#991B1B' }}>
              {unansweredTotal} unanswered message{unansweredTotal !== 1 ? 's' : ''} across {unansweredAmCount} account manager{unansweredAmCount !== 1 ? 's' : ''}
            </div>
            <div style={{ fontSize: 11, color: '#7f1d1d', marginTop: 2 }}>
              SLA threshold: replies expected within 4 hours.
            </div>
          </div>
        </div>
      )}

      {/* Super user: per-AM unanswered list */}
      {isSuperUser && unansweredByAm.length > 0 && (
        <div data-testid="messages-unanswered-by-am" style={{ marginBottom: 16, paddingBottom: 12, borderBottom: `1px solid ${C.borderLight}` }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
            Unanswered by team member
          </div>
          {unansweredByAm.map((am) => {
            const sev = severityColor(am.severity);
            return (
              <button
                key={am.amId}
                type="button"
                data-testid={`messages-unanswered-am-${am.amId}`}
                onClick={() => onAmClick && onAmClick(am)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 10px', marginBottom: 4,
                  background: 'transparent', border: 'none', borderRadius: 8,
                  textAlign: 'left', cursor: 'pointer', transition: 'background 0.1s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = C.bg; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <Avatar initials={am.amInitials} color={C.accent} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{am.amName}</div>
                  <div style={{ fontSize: 11, color: C.textMid, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {am.count} unanswered · {(am.clients || []).map((c) => c.clientName).slice(0, 2).join(', ')}
                    {(am.clients || []).length > 2 ? ` +${am.clients.length - 2} more` : ''}
                  </div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: sev.bg, color: sev.fg, display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: sev.dot }} />
                  {Math.round(am.oldestAgeHours)}h
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Your messages */}
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
          Your messages
          {data.myUnreadCount ? <span style={{ marginLeft: 8, color: '#dc2626', fontWeight: 800 }}>{data.myUnreadCount} unread</span> : null}
        </div>
        {yourMessages.length === 0 ? (
          <div style={{ padding: '20px 0', textAlign: 'center', color: C.textLight, fontSize: 12 }}>
            <Icon name="checkmark-circle-outline" style={{ fontSize: 26, color: C.green, display: 'block', margin: '0 auto 6px' }} />
            All caught up — no unread messages.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {yourMessages.map((m, idx) => (
              <button
                key={`${m.clientId}-${idx}`}
                type="button"
                data-testid={`messages-your-${m.clientId}-${idx}`}
                onClick={() => onMessageClick && onMessageClick(m)}
                style={{
                  display: 'grid', gridTemplateColumns: '40px 1fr auto', gap: 10,
                  padding: '10px 8px',
                  border: 'none', borderBottom: `1px solid ${C.borderLight}`,
                  background: 'transparent', textAlign: 'left',
                  cursor: 'pointer', fontFamily: 'inherit', transition: 'background 0.1s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = C.bg; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <Avatar initials={m.contactInitials || '?'} color={C.green} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                    {m.contactName || 'Unknown'}
                    <span style={{ color: C.textLight, fontWeight: 600, marginLeft: 6 }}>· {m.clientName}</span>
                  </div>
                  <div style={{ fontSize: 12, color: C.textMid, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {m.preview || ''}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                  <span style={{ fontSize: 10, color: C.textLight, whiteSpace: 'nowrap' }}>{formatRelative(m.createdAt)}</span>
                  {m.isUnread && <span style={{ width: 8, height: 8, borderRadius: '50%', background: C.accent }} />}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Footer metrics */}
      <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${C.borderLight}`, display: 'flex', gap: 18, fontSize: 11, color: C.textMid, flexWrap: 'wrap' }}>
        <span>You: <strong style={{ color: C.text }}>{data.myUnreadCount || 0}</strong> unread · <strong style={{ color: C.text }}>{data.myUnansweredCount || 0}</strong> unanswered</span>
        {isSuperUser && (
          <span>Team: <strong style={{ color: '#dc2626' }}>{unansweredTotal}</strong> unanswered across {unansweredAmCount} member{unansweredAmCount !== 1 ? 's' : ''}</span>
        )}
      </div>
    </div>
  );
}
